import { match, P } from "ts-pattern";
import type { DistributedPick } from "type-fest";
import { omit, pick } from "~/lib/std";
import { assertNever } from "~/utils/assertions";
import { VisualizationType } from "./constants";
import {
  DEFAULT_SHOW_ROTATION_CONTROLS,
  DEFAULT_TERMINAL_AUTO_SCROLL,
  DEFAULT_USE_STATIC_TRANSFORMS,
} from "./defaults";
import type * as sdkTypes from "./sdk";
import type { Panel } from "./types";

export function serializePanels(
  panels: ReadonlyArray<Panel>,
): ReadonlyArray<sdkTypes.PanelDescriptor> {
  return panels.map((panel): sdkTypes.PanelDescriptor => {
    const pickedPanel = pick(
      panel,
      "id",
      "title",
      "visualization",
      "topics",
    ) as DistributedPick<Panel, "id" | "title" | "visualization" | "topics">;

    return match(pickedPanel)
      .with({ visualization: VisualizationType.Chart }, (panel) => ({
        ...panel,
        topics: [omit(panel.topics[0], "showFields")] as const,
      }))
      .with({ visualization: VisualizationType.Image }, (panel) => ({
        ...panel,
        topics: [panel.topics[0]] as const,
      }))
      .with({ visualization: VisualizationType.Terminal }, (panel) => ({
        ...panel,
        topics: [omit(panel.topics[0], "selectedRecordTimestamp")] as const,
      }))
      .with(
        {
          visualization: P.union(
            null,
            VisualizationType.Timeline,
            VisualizationType.Map,
            VisualizationType.ThreeD,
          ),
        },
        (panel) => panel,
      )
      .exhaustive();
  });
}

export function deserializePanels(
  descriptors: ReadonlyArray<sdkTypes.PanelDescriptor>,
): ReadonlyArray<Panel> {
  return descriptors.map((descriptor): Panel => {
    const base = {
      ...descriptor,
      topicSelectionConfig: {
        search: null,
        [VisualizationType.Chart]: false,
        [VisualizationType.Image]: false,
        [VisualizationType.ThreeD]: false,
        [VisualizationType.Map]: false,
        lastSelected: null,
      },
    };

    if (base.visualization === null) {
      return base;
    } else {
      const initializedBase = {
        ...base,
        hasAutoSkipped: false,
        selectedTag: null,
      };

      switch (initializedBase.visualization) {
        case VisualizationType.Timeline:
        case VisualizationType.Image:
        case VisualizationType.Map: {
          return initializedBase;
        }
        case VisualizationType.Terminal: {
          return {
            ...initializedBase,
            topics: [
              {
                ...initializedBase.topics[0],
                selectedRecordTimestamp: null,
              },
            ],
            autoScroll: DEFAULT_TERMINAL_AUTO_SCROLL,
          };
        }
        case VisualizationType.Chart: {
          return {
            ...initializedBase,
            showOverview: false,
            topics: [
              {
                ...initializedBase.topics[0],
                showFields: true,
              },
            ],
          };
        }
        case VisualizationType.ThreeD: {
          return {
            ...initializedBase,
            useStaticTransforms: DEFAULT_USE_STATIC_TRANSFORMS,
            showRotationControls: DEFAULT_SHOW_ROTATION_CONTROLS,
          };
        }
        default: {
          assertNever(initializedBase);
        }
      }
    }
  });
}
